import { useEffect, useRef, useState } from "react";

export function makeDotGrid(width, height) {
  const spacing = width < 640 ? 24 : width > 1920 ? 28 : 26;
  const dots = [];
  for (let y = spacing / 2; y < height; y += spacing) {
    for (let x = spacing / 2; x < width; x += spacing) dots.push({ x0:x, y0:y, x, y, vx:0, vy:0 });
  }
  return dots;
}

export function stepDots(dots, pointer, delta = 1, effects = [], time = 0) {
  const dt = Math.min(2, Math.max(0, delta));
  const damping = 0.74 ** dt;
  let moving = false;
  for (const dot of dots) {
    let tx = dot.x0, ty = dot.y0;
    if (pointer.active) {
      const dx = dot.x0 - pointer.x, dy = dot.y0 - pointer.y, distance = Math.hypot(dx, dy);
      if (distance > 0 && distance < 120) {
        const offset = 8 * (1 + Math.cos(Math.PI * distance / 120)) / 2;
        tx += dx / distance * offset;
        ty += dy / distance * offset;
      }
    }
    for (const effect of effects) {
      const progress = (time - effect.start) / 1400;
      if (progress < 0 || progress >= 1) continue;
      const dx = dot.x0 - effect.x, dy = dot.y0 - effect.y;
      const distance = Math.hypot(dx, dy);
      if (distance === 0 || distance > 240) continue;
      const envelope = Math.sin(Math.PI * progress);
      const falloff = (1 + Math.cos(Math.PI * distance / 240)) / 2;
      if (effect.mode === 0) {
        const ring = Math.exp(-(((distance - progress * 240) / 36) ** 2));
        tx += dx / distance * 38 * ring * envelope;
        ty += dy / distance * 38 * ring * envelope;
      } else {
        const offset = 32 * falloff * envelope;
        tx += (effect.mode === 1 ? -dy : -dx) / distance * offset;
        ty += (effect.mode === 1 ? dx : -dy) / distance * offset;
      }
    }
    // Repeated taps can overlap without scattering the grid across the page.
    const displacement = Math.hypot(tx - dot.x0, ty - dot.y0);
    if (displacement > 48) {
      tx = dot.x0 + (tx - dot.x0) * 48 / displacement;
      ty = dot.y0 + (ty - dot.y0) * 48 / displacement;
    }
    dot.vx = (dot.vx + (tx - dot.x) * 0.085 * dt) * damping;
    dot.vy = (dot.vy + (ty - dot.y) * 0.085 * dt) * damping;
    dot.x += dot.vx * dt;
    dot.y += dot.vy * dt;
    if (Math.abs(tx-dot.x)+Math.abs(ty-dot.y)+Math.abs(dot.vx)+Math.abs(dot.vy) > 0.02) moving = true;
    else { dot.x=tx; dot.y=ty; dot.vx=0; dot.vy=0; }
  }
  return moving;
}

export default function InteractiveDotGrid({ dark }) {
  const canvasRef = useRef(null);
  const [reduced,setReduced] = useState(() => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || false);
  useEffect(() => {
    const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!media) return;
    const change=()=>setReduced(media.matches);
    change(); media.addEventListener("change",change);
    return ()=>media.removeEventListener("change",change);
  },[]);
  useEffect(() => {
    const canvas=canvasRef.current, page=canvas.closest(".login-page");
    const ctx=canvas.getContext("2d",{alpha:true});
    if (!ctx || !page) return;
    const enabled=!reduced;
    const pointer={x:0,y:0,active:false};
    let dots=[],effects=[],tapCount=0,width=0,height=0,frame=null,last=0;
    function draw() {
      ctx.clearRect(0,0,width,height);
      ctx.fillStyle=dark?"rgba(180,194,214,0.22)":"rgba(47,69,103,0.26)";
      ctx.beginPath();
      for (const dot of dots) { ctx.moveTo(dot.x+1.15,dot.y); ctx.arc(dot.x,dot.y,1.15,0,Math.PI*2); }
      ctx.fill();
    }
    function tick(time) {
      frame=null;
      if (!enabled || document.visibilityState==="hidden") return;
      effects=effects.filter(effect=>time-effect.start<1400);
      const moving=stepDots(dots,pointer,(time-last)/16.667,effects,time);
      last=time; draw();
      if (moving || effects.length) frame=requestAnimationFrame(tick);
    }
    function schedule() {
      if (enabled && frame===null && document.visibilityState!=="hidden") {
        last=performance.now(); frame=requestAnimationFrame(tick);
      }
    }
    function resize() {
      width=window.innerWidth; height=window.innerHeight;
      const ratio=Math.min(window.devicePixelRatio||1,2);
      canvas.width=Math.round(width*ratio); canvas.height=Math.round(height*ratio);
      canvas.style.width=`${width}px`; canvas.style.height=`${height}px`;
      ctx.setTransform(ratio,0,0,ratio,0,0);
      effects=[]; dots=makeDotGrid(width,height); draw(); if(pointer.active)schedule();
    }
    function move(event) {
      if (!enabled) return;
      pointer.x=event.clientX; pointer.y=event.clientY; pointer.active=true; schedule();
    }
    function tap(event) {
      if (!enabled || document.visibilityState==="hidden" || event.isPrimary===false || event.button>0) return;
      if (event.target.closest("button, a, input, select, textarea, [role='tab'], [contenteditable='true']")) return;
      move(event);
      effects.push({x:event.clientX,y:event.clientY,start:performance.now(),mode:tapCount++%3});
      effects=effects.slice(-3);
    }
    function leave() { pointer.active=false; schedule(); }
    function endTouch(event) { if(event.pointerType!=="mouse")leave(); }
    function visibility() {
      pointer.active=false;
      effects=[];
      if(frame!==null)cancelAnimationFrame(frame);
      frame=null; schedule();
    }
    resize();
    window.addEventListener("resize",resize,{passive:true});
    page.addEventListener("pointermove",move,{passive:true});
    page.addEventListener("pointerdown",tap,{passive:true});
    page.addEventListener("pointerleave",leave,{passive:true});
    window.addEventListener("pointerup",endTouch,{passive:true});
    window.addEventListener("pointercancel",leave,{passive:true});
    window.addEventListener("blur",leave);
    document.addEventListener("visibilitychange",visibility);
    return ()=>{
      if(frame!==null)cancelAnimationFrame(frame);
      window.removeEventListener("resize",resize);
      page.removeEventListener("pointermove",move); page.removeEventListener("pointerdown",tap); page.removeEventListener("pointerleave",leave);
      window.removeEventListener("pointerup",endTouch); window.removeEventListener("pointercancel",leave); window.removeEventListener("blur",leave);
      document.removeEventListener("visibilitychange",visibility);
    };
  },[dark,reduced]);
  return <canvas ref={canvasRef} className="login-particles" aria-hidden="true"/>;
}
